/**
 * Model Details Component
 * Shows parameters, predictions, and their meanings/implications
 */

import React, { useState, useEffect, useCallback } from 'react';
import '../styles/ModelDetails.css';
import { API_CONFIG } from '../config/api';

const ModelDetails = ({ moduleId, onClose }) => {
  const [modelDetail, setModelDetail] = useState(null);
  const [activeTab, setActiveTab] = useState('parameters');
  const [loading, setLoading] = useState(true);

  const fetchModelDetail = useCallback(async () => {
    try {
      const response = await fetch(API_CONFIG.ENDPOINTS.MODEL_DETAILS(moduleId));
      const data = await response.json();
      setModelDetail(data.model || data);
    } catch (error) {
      console.error('Error fetching model details:', error);
    } finally {
      setLoading(false);
    }
  }, [moduleId]);

  useEffect(() => {
    fetchModelDetail();
  }, [fetchModelDetail]);

  if (loading) {
    return <div className="model-details-loading">Loading...</div>;
  }

  if (!modelDetail) {
    return <div className="model-details-error">Error loading model details</div>;
  }

  return (
    <div className="model-details-modal">
      <div className="model-details-overlay" onClick={onClose}></div>
      <div className="model-details-panel">
        <div className="details-header">
          <div>
            <h2>{modelDetail.icon} {modelDetail.name}</h2>
            <p className="details-subtitle">{modelDetail.description}</p>
          </div>
          <button className="details-close" onClick={onClose}>✕</button>
        </div>

        <div className="details-tabs">
          <button
            className={`details-tab ${activeTab === 'parameters' ? 'active' : ''}`}
            onClick={() => setActiveTab('parameters')}
          >
            📋 Input Parameters
          </button>
          <button
            className={`details-tab ${activeTab === 'predictions' ? 'active' : ''}`}
            onClick={() => setActiveTab('predictions')}
          >
            🔮 Predictions
          </button>
        </div>

        <div className="details-content">
          {activeTab === 'parameters' && (
            <div className="tab-content">
              <h3>Input Parameters</h3>
              <p className="tab-description">
                These are the data inputs required to generate predictions. Provide accurate values for best results.
              </p>

              <div className="parameters-list">
                {Object.entries(modelDetail.parameters || {}).map(([key, param]) => (
                  <div key={key} className="parameter-card">
                    <div className="param-header">
                      <label>{param.label}</label>
                      {param.required && <span className="required-badge">Required</span>}
                    </div>
                    <p className="param-description">{param.description}</p>

                    <div className="param-details">
                      <span className="param-type">{param.type}</span>
                      {param.min !== undefined && (
                        <span className="param-range">
                          {param.min} {param.unit ? `${param.unit}` : ''} - {param.max} {param.unit ? `${param.unit}` : ''}
                        </span>
                      )}
                      {param.options && (
                        <div className="param-options">
                          Options: {param.options.join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'predictions' && (
            <div className="tab-content">
              <h3>Output Predictions</h3>
              <p className="tab-description">
                The model outputs these predictions, which represent estimated values for the specified inputs.
              </p>

              <div className="predictions-list">
                {Object.entries(modelDetail.predictions || {}).map(([key, pred]) => (
                  <div key={key} className="prediction-card">
                    <div className="pred-header">
                      <label>{pred.label}</label>
                      <span className="pred-unit">{pred.unit}</span>
                    </div>
                    <p className="pred-description">{pred.description}</p>

                    <div className="pred-implications">
                      <strong>📌 Implications:</strong>
                      <p>{pred.implications}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="details-footer">
          <p className="info-note">
            💡 <strong>Tip:</strong> Hover over parameter labels to see full descriptions.
            Check the Technical Documentation tab for methodology details.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ModelDetails;
